import { ArrowRight, CalendarClock, CheckCircle2, CircleDollarSign, MapPin, Search, Wrench, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { InfoCard, PageHeader, ResponsiveFilters, StateView, StatusBadge } from "../components";
import { brl, dateTime, useApiData } from "../hooks";
import { workOrderNetAmount } from "../money";
import type { CursorPage, WorkOrder } from "../types";
import { simplifiedOperationalStatus } from "../work-order-status";

export type OrderFilter = "open" | "planned" | "in_progress" | "awaiting_payment" | "completed" | "cancelled" | "all";
export const defaultOrderFilter: OrderFilter = "all";
export const workOrderFilterOptions: { key: OrderFilter; label: string; mobileLabel: string }[] = [
  { key: "all", label: "Todas", mobileLabel: "Todas" }, { key: "open", label: "Em aberto", mobileLabel: "Abertas" }, { key: "planned", label: "Programadas", mobileLabel: "Program." }, { key: "in_progress", label: "Em andamento", mobileLabel: "Andamento" },
  { key: "awaiting_payment", label: "Aguard. pagamento", mobileLabel: "Aguard. pgto." }, { key: "completed", label: "Concluídas", mobileLabel: "Concluídas" },
  { key: "cancelled", label: "Canceladas", mobileLabel: "Canceladas" },
];
const awaitingPaymentStatuses = new Set(["not_billed", "partially_billed", "billed", "partially_paid", "overdue"]);
const compactDate = (value: string) => new Date(`${value}T12:00:00`).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
export const matchesOrderFilter = (order: WorkOrder, filter: OrderFilter) => {
  if (filter === "all") return true;
  const status = simplifiedOperationalStatus(order.operationalStatus);
  if (filter === "open") return !["completed", "cancelled", "not_performed"].includes(status);
  if (filter === "cancelled") return status === "cancelled" || status === "not_performed";
  if (filter === "awaiting_payment") return status === "completed" && awaitingPaymentStatuses.has(order.financialStatus);
  if (filter === "completed") return status === "completed" && order.financialStatus === "paid";
  return status === filter;
};

export function WorkOrdersPage() {
  const money = brl;
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [filter, setFilter] = useState<OrderFilter>(defaultOrderFilter);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [search]);
  const listPath = useMemo(() => {
    const params = new URLSearchParams({ pageSize: "100" });
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (debouncedSearch) params.set("q", debouncedSearch);
    return `/work-orders?${params.toString()}`;
  }, [from, to, debouncedSearch]);
  const { data, error, loading } = useApiData<CursorPage<WorkOrder>>(listPath);
  const orders = data?.data ?? [];
  const filteredOrders = orders.filter((order) => matchesOrderFilter(order, filter));
  const inField = orders.filter(({ operationalStatus }) => simplifiedOperationalStatus(operationalStatus) === "in_progress");
  const planned = orders.filter(({ operationalStatus }) => simplifiedOperationalStatus(operationalStatus) === "planned");
  const completed = orders.filter(({ operationalStatus }) => simplifiedOperationalStatus(operationalStatus) === "completed");
  const overdue = orders.filter(({ financialStatus }) => financialStatus === "overdue");
  const clearFilters = () => {
    setFrom("");
    setTo("");
    setFilter(defaultOrderFilter);
    setSearch("");
  };
  const activeFilterCount = Number(Boolean(from)) + Number(Boolean(to)) + Number(Boolean(search.trim()));
  const periodSummary = from && to ? `${compactDate(from)}–${compactDate(to)}` : from ? `Desde ${compactDate(from)}` : to ? `Até ${compactDate(to)}` : "Qualquer período";

  return <>
    <section className="page-intro"><div className="app-container"><PageHeader title="Ordens de serviço" /></div></section>
    <section className="app-section compact"><div className="app-container work-orders-workspace">
      <div className="order-summary"><InfoCard icon={Wrench} label="Em campo" value={inField.length} tone="soft" /><InfoCard icon={CalendarClock} label="Programadas" value={planned.length} /><InfoCard icon={CheckCircle2} label="Concluídas" value={completed.length} /><InfoCard icon={CircleDollarSign} label="Vencidas" value={overdue.length} tone="red" /></div>
      <div className="workspace-card work-order-filter-panel">
        <div className="admin-tabs quote-period-tabs work-order-status-tabs">{workOrderFilterOptions.map(({ key, label, mobileLabel }) => <button key={key} className={filter === key ? "active" : ""} onClick={() => setFilter(key)}><span className="filter-label-desktop">{label}</span><span className="filter-label-mobile">{mobileLabel}</span></button>)}</div>
        <ResponsiveFilters className="work-order-secondary-filters" activeCount={activeFilterCount} label="Busca e período" summary={periodSummary}>
          <div className="quote-filter-fields work-order-filter-fields">
            <label className="quote-search"><span>Buscar</span><div><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cliente ou número da OS" /></div></label>
            <label><span>De</span><input type="date" value={from} max={to || undefined} onChange={(event) => setFrom(event.target.value)} /></label>
            <label><span>Até</span><input type="date" value={to} min={from || undefined} onChange={(event) => setTo(event.target.value)} /></label>
            <button className="quote-clear-filter" onClick={clearFilters}><X size={15} /> Limpar</button>
          </div>
        </ResponsiveFilters>
      </div>
      <StateView loading={loading} error={error} empty={filteredOrders.length === 0}>
        <div className="work-order-grid">{filteredOrders.map((order) => <article className="work-order-card" key={order.id}>
          <div className="work-order-card-head">
            <span className="work-order-number">OS {order.number}</span>
            <div className="work-order-card-statuses"><StatusBadge value={simplifiedOperationalStatus(order.operationalStatus)} /><StatusBadge value={order.financialStatus} /></div>
          </div>
          <div className="work-order-card-body">
            <div className="work-order-card-title"><span className="work-order-icon"><Wrench size={20} /></span><h2>{order.customerName ?? "Cliente"}</h2></div>
            <p className="work-order-card-address"><MapPin size={13} aria-hidden />{order.location?.address ?? "Endereço não informado"}</p>
            <p>Até {new Date(order.scheduledEnd).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} — {dateTime(order.scheduledStart)}</p>
            <div className="work-order-card-value"><span>Valor</span><strong>{money(workOrderNetAmount(order))}</strong></div>
          </div>
          <Link to={`/ordens/${order.id}`} className="work-order-open">Abrir ordem de serviço <ArrowRight size={16} /></Link>
        </article>)}</div>
      </StateView>
    </div></section>
  </>;
}
